import type { SelectChangeEvent, SelectProps } from "@mui/material"
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material"
import { KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import { type ReactElement, type ReactNode, forwardRef, useId, useRef } from "react"

import { MENU_MAX_HEIGHT } from "@/constants/forms"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import type { Option } from "@/types/form-fields"

export type AerosSelectProps<T extends string | number = string> = Omit<SelectProps<T | null>, "onChange"> & {
  options: Option<T>[]
  label?: ReactNode | string
  placeholder?: string
  helperText?: string
  required?: boolean
  onChange?: (value: T | null) => void
  loadMore?: () => void
  fetching?: boolean
  "data-testid"?: string
}

export const AerosSelect = forwardRef(
  <T extends string | number = string>(
    {
      options,
      label,
      placeholder,
      helperText,
      required,
      error,
      onChange,
      className,
      loadMore,
      fetching = false,
      "data-testid": testId,
      ...props
    }: AerosSelectProps<T>,
    ref: React.Ref<HTMLDivElement>
  ): ReactElement => {
    const labelId = useId()
    const dropdownMenuRef = useRef<HTMLDivElement>(null)

    const handleScroll = useScrollLoadMore({
      fetching,
      loadMore: loadMore ?? (() => {}),
      scrollContainerRef: dropdownMenuRef,
    })

    const handleChange = (e: SelectChangeEvent<T | null>) => {
      const value = e.target.value
      onChange?.(value === "" ? null : (value as T))
    }

    return (
      <FormControl ref={ref} className={className} fullWidth required={required} error={error} data-testid={testId}>
        {label && <InputLabel id={labelId}>{label}</InputLabel>}
        <Select
          {...props}
          value={props.value ?? ""}
          labelId={label ? labelId : undefined}
          variant='outlined'
          displayEmpty
          IconComponent={KeyboardArrowDownOutlined}
          MenuProps={{
            slotProps: {
              paper: {
                style: { maxHeight: MENU_MAX_HEIGHT },
                ref: dropdownMenuRef,
              },
            },
          }}
          onChange={handleChange}
          onScrollCapture={loadMore ? handleScroll : undefined}
          renderValue={(value) =>
            !value && placeholder ? (
              <span className='text-gray-500'>{placeholder}</span>
            ) : (
              <span>{options.find((option) => option.value === value)?.label}</span>
            )
          }
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    )
  }
) as <T extends string | number = string>(props: AerosSelectProps<T> & { ref?: React.Ref<unknown> }) => ReactElement
